/**
 * Mar 3, 2026 · Purchase Executive Dashboard.
 * Feb 2026 · Period-toggle rhythm (Total / Today / Yesterday / Week /
 * MTD / Last Month) — mirrors Executive Sales Overview pattern.
 *
 * When a period is selected the placed / received / pending-payment tiles
 * refresh to that window. Operational tiles (Open POs / Overdue / GRN QC)
 * stay FY-wide because they are point-in-time status counts.
 *
 * Single roundtrip → `/api/dashboard/purchase?period=<key>`.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import TwoLineDocNo from "@/components/TwoLineDocNo";
import {
  ShoppingBagIcon, PackageIcon, ClockCountdownIcon,
  ShieldCheckIcon, ArrowRightIcon, ArrowsCounterClockwiseIcon,
  CurrencyInrIcon,
} from "@phosphor-icons/react";

const fmtInr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const fmtQty = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const fmtRate = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PERIODS = [
  { k: "total",     label: "Total View" },
  { k: "today",     label: "Today" },
  { k: "yesterday", label: "Yesterday" },
  { k: "week",      label: "This Week" },
  { k: "mtd",       label: "MTD" },
  { k: "lastmonth", label: "Last Month" },
];

const PERIOD_ACCENT = {
  total:     "text-[var(--accent)]",
  today:     "text-[var(--accent)]",
  yesterday: "text-amber-300",
  week:      "text-cyan-300",
  mtd:       "text-indigo-300",
  lastmonth: "text-fuchsia-300",
};

function Tile({ label, value, sub, color = "text-white", tid, to }) {
  const Wrap = to ? Link : "div";
  const props = to ? { to } : {};
  return (
    <Wrap {...props} data-testid={tid} className={`tactical-card p-4 flex flex-col justify-between min-h-[110px] ${to ? "hover:border-[var(--accent)] transition" : ""}`}>
      <div className="overline text-[10px] text-zinc-400">{label}</div>
      <div className={`font-mono text-2xl sm:text-3xl font-black tabular-nums leading-tight ${color}`}>{value}</div>
      {sub && <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">{sub}</div>}
    </Wrap>
  );
}

function MiniTile({ label, value, sub, icon: Icon, tone = "text-white", to, testId }) {
  const Wrap = to ? Link : "div";
  const props = to ? { to } : {};
  return (
    <Wrap {...props} data-testid={testId} className={`tactical-card p-3 flex flex-col gap-1.5 ${to ? "hover:border-[var(--accent)] transition" : ""}`}>
      <div className="flex items-center justify-between">
        <div className="overline text-zinc-400">{label}</div>
        {Icon && <Icon size={14} weight="bold" className="text-zinc-500" />}
      </div>
      <div className={`font-mono text-xl sm:text-2xl font-black tabular-nums ${tone}`}>{value}</div>
      {sub && <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{sub}</div>}
    </Wrap>
  );
}

export default function PurchaseDashboard() {
  const [d, setD] = useState(null);
  const [period, setPeriod] = useState("total");

  const load = async (p = period) => {
    try {
      const { data } = await api.get(`/dashboard/purchase?period=${p}`);
      setD(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => {
    load(period);
    const t = setInterval(() => load(period), 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  if (!d) return <div className="p-8 text-zinc-400" data-testid="purchase-dashboard-loading">Loading purchase snapshot…</div>;

  const placed = d.placed || {};
  const received = d.received || {};
  const pay = d.pending_payments || {};
  const fulfilPct = placed.qty > 0 ? Math.round((received.qty / placed.qty) * 100) : 0;
  const accent = PERIOD_ACCENT[period] || "text-[var(--accent)]";
  const periodLabel = d.period_label || "Total";

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="purchase-dashboard">
      {/* Header · centered executive layout — mirrors Sales Dashboard */}
      <div className="text-center pt-2 pb-6 border-b border-[var(--border)]">
        <div className="overline text-[10px] tracking-[0.3em] text-zinc-500">NARROW FABRIC · REAL-TIME KPIs</div>
        <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tight text-white mt-2">Purchase Overview</h1>
        <div className="flex items-center justify-center gap-3 mt-3 flex-wrap">
          <div className="inline-flex rounded-md border border-[var(--border)] overflow-hidden text-xs font-mono" data-testid="purchase-view-toggle">
            {PERIODS.map((b, i) => (
              <button
                key={b.k}
                onClick={() => setPeriod(b.k)}
                data-testid={`purchase-view-${b.k}`}
                className={`h-10 sm:h-9 px-3 uppercase tracking-widest ${i > 0 ? "border-l border-[var(--border)]" : ""} ${period === b.k ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-2)]"}`}
              >{b.label}</button>
            ))}
          </div>
          <button onClick={() => load(period)} className="h-10 sm:h-9 px-3 rounded-md border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs flex items-center gap-1.5" data-testid="purchase-dashboard-refresh">
            <ArrowsCounterClockwiseIcon size={12} /> Refresh
          </button>
          <Link to="/purchase-orders" className="text-[11px] font-mono uppercase tracking-widest text-[var(--accent)] hover:underline flex items-center gap-1" data-testid="purchase-open-pos-link">
            Open POs<ArrowRightIcon size={11} weight="bold" />
          </Link>
        </div>
      </div>

      {/* Row 1 · POs Issued in window */}
      <div>
        <div className={`overline mb-2 ${accent}`}>POs Issued · {periodLabel} {placed.count ? <span className="ml-2 text-[10px] font-mono text-zinc-500">({placed.count} PO{placed.count !== 1 ? "s" : ""})</span> : null}</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Tile label="POs Issued" value={fmtQty(placed.qty)}      sub="Qty (units · kg)"    color="text-white" tid="po-placed-qty" />
          <Tile label="POs Issued" value={fmtInr(placed.value)}    sub="Value"               color={accent} tid="po-placed-value" />
          <Tile label="POs Issued" value={fmtRate(placed.avg_price)} sub="Avg Price / unit"  color={accent} tid="po-placed-avg" />
        </div>
      </div>

      {/* Row 2 · Goods Received in window */}
      <div>
        <div className="overline mb-2 text-emerald-300">Goods Received · {periodLabel} {received.count ? <span className="ml-2 text-[10px] font-mono text-zinc-500">({received.count} GRN{received.count !== 1 ? "s" : ""}{fulfilPct > 0 ? ` · ${fulfilPct}% fulfilled` : ""})</span> : null}</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Tile label="Goods Received" value={fmtQty(received.qty)}      sub="Qty (units · kg)"   color="text-white"         tid="po-rcv-qty" />
          <Tile label="Goods Received" value={fmtInr(received.value)}    sub="Value"              color="text-emerald-300"   tid="po-rcv-value" />
          <Tile label="Goods Received" value={fmtRate(received.avg_price)} sub="Avg Price / unit" color="text-emerald-300"   tid="po-rcv-avg" />
        </div>
      </div>

      {/* Row 3 · Pending Supplier Payments */}
      <div>
        <div className="overline mb-2 text-amber-300">Pending Supplier Payments · {periodLabel} {pay.count ? <span className="ml-2 text-[10px] font-mono text-zinc-500">({pay.count} supplier{pay.count !== 1 ? "s" : ""})</span> : null}</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Tile label="Outstanding" value={fmtInr(pay.value)} sub="Value payable" color="text-amber-300" tid="po-pending-pay-value" />
          <Tile label="Suppliers" value={fmtQty(pay.count)} sub="With outstanding" color="text-white" tid="po-pending-pay-count" />
          <div className="tactical-card p-4 flex flex-col justify-between min-h-[110px]" data-testid="po-payment-hint">
            <div className="overline text-[10px] text-zinc-400 flex items-center gap-1"><CurrencyInrIcon size={11} weight="bold" /> Ledger</div>
            <div className="font-mono text-xs text-zinc-400 leading-tight">Received minus paid, scoped to the selected window. Mark POs as Paid in the PO detail dialog to clear this tile.</div>
          </div>
        </div>
      </div>

      {/* Operational KPIs (point-in-time, always FY-wide) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniTile label="Pending PR" value={d.pending_pr} icon={ShoppingBagIcon} to="/purchase-requirements" testId="tile-pending-pr" />
        <MiniTile label="Open POs" value={d.open_po_count} sub={fmtInr(d.open_po_value) + " open value"} icon={PackageIcon} tone="text-[var(--accent)]" to="/purchase-orders" testId="tile-open-po" />
        <MiniTile label="Overdue POs" value={d.overdue_po_count} sub={d.overdue_po_count > 0 ? fmtInr(d.overdue_po_value) + " at risk" : "on schedule"} icon={ClockCountdownIcon}
          tone={d.overdue_po_count > 0 ? "text-red-400" : "text-emerald-400"} to="/purchase-orders?tab=report" testId="tile-overdue-po" />
        <MiniTile label="GRN Pending QC" value={d.grn_pending_qc} icon={ShieldCheckIcon} tone={d.grn_pending_qc > 0 ? "text-amber-300" : "text-emerald-400"} testId="tile-grn-pending-qc" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="tactical-card p-4">
          <div className="overline mb-3">Top Suppliers · by PO Value</div>
          {d.top_suppliers.length === 0 ? <div className="text-xs text-zinc-500 py-6 text-center">No supplier data</div> :
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                <th className="text-left py-2">Supplier</th><th className="text-right">POs</th><th className="text-right">Value</th><th className="text-right">Delivery %</th>
              </tr></thead>
              <tbody>
                {d.top_suppliers.map(s => (
                  <tr key={s.supplier} className="border-b border-[var(--border)]/40" data-testid={`supplier-row-${s.supplier}`}>
                    <td className="py-2 text-white truncate max-w-[240px]" title={s.supplier}>{s.supplier}</td>
                    <td className="text-right font-mono">{s.count}</td>
                    <td className="text-right font-mono">{fmtInr(s.value)}</td>
                    <td className={`text-right font-mono ${s.delivery_pct >= 80 ? "text-emerald-300" : s.delivery_pct >= 50 ? "text-amber-300" : "text-red-300"}`}>{s.delivery_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>}
        </div>
        <div className="tactical-card p-4">
          <div className="overline mb-3">Recent POs</div>
          {d.recent_pos.length === 0 ? <div className="text-xs text-zinc-500 py-6 text-center">No POs raised yet</div> :
            <ul className="space-y-2">
              {d.recent_pos.map(p => (
                <li key={p.id} className="flex items-center justify-between border border-[var(--border)] rounded-sm px-3 py-2" data-testid={`recent-po-${p.po_no}`}>
                  <div className="min-w-0">
                    <TwoLineDocNo value={p.po_no} className="text-white" />
                    <div className="text-[11px] text-zinc-400 truncate">{p.supplier_name || "—"}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm text-white">{fmtInr(p.total_value)}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{p.status}</div>
                  </div>
                </li>
              ))}
            </ul>}
        </div>
      </div>
    </div>
  );
}
